export interface User {
  id: string
  email: string
  name: string
  role: 'customer' | 'admin'
  createdAt: string
}

export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export const mockUsers: User[] = [
  {
    id: '1',
    email: 'admin@saltymermaidart.com',
    name: 'Admin User',
    role: 'admin',
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '2', 
    email: 'customer@example.com',
    name: 'John Doe',
    role: 'customer',
    createdAt: '2024-02-01T00:00:00Z'
  }
]

interface PendingAction {
  type: 'purchase' | 'booking'
  data: any
  returnTo?: string
}

class AuthService {
  private static instance: AuthService
  private user: User | null = null
  private listeners: ((user: User | null) => void)[] = []
  private pendingAction: PendingAction | null = null

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  constructor() {
    this.loadUserFromStorage()
  }

  private loadUserFromStorage(): void {
    try {
      const storedUser = localStorage.getItem('saltymermaid_user')
      if (storedUser) {
        this.user = JSON.parse(storedUser)
      }
    } catch (error) {
      console.warn('Failed to load user from storage:', error)
      localStorage.removeItem('saltymermaid_user')
    }
  }

  private saveUserToStorage(user: User | null): void {
    try {
      if (user) {
        localStorage.setItem('saltymermaid_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('saltymermaid_user')
      }
    } catch (error) {
      console.warn('Failed to save user to storage:', error)
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.user))
  }

  subscribe(listener: (user: User | null) => void): () => void {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  getCurrentUser(): User | null {
    return this.user
  }

  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))

      const user = mockUsers.find(u => u.email === email)
      if (!user) {
        return { success: false, error: 'Invalid email or password' }
      }

      if (password !== 'i1FH!rM6xXamHqMokyrQ9!qz') {
        return { success: false, error: 'Invalid email or password' }
      }

      this.user = user
      this.saveUserToStorage(user)
      this.notifyListeners()

      return { success: true }
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' }
    }
  }

  async register(email: string, password: string, name: string): Promise<{ success: boolean; error?: string }> {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))

      const existingUser = mockUsers.find(u => u.email === email)
      if (existingUser) {
        return { success: false, error: 'An account with this email already exists' }
      }

      const newUser: User = {
        id: Date.now().toString(),
        email,
        name,
        role: 'customer',
        createdAt: new Date().toISOString()
      }

      mockUsers.push(newUser)
      this.user = newUser
      this.saveUserToStorage(newUser)
      this.notifyListeners()

      return { success: true }
    } catch (error) {
      return { success: false, error: 'Registration failed. Please try again.' }
    }
  }

  logout(): void {
    this.user = null
    this.saveUserToStorage(null)
    this.notifyListeners()
  }

  isAuthenticated(): boolean {
    return this.user !== null
  }

  isAdmin(): boolean {
    return this.user?.role === 'admin'
  }

  setPendingAction(action: PendingAction): void {
    this.pendingAction = action
  }

  getPendingAction(): PendingAction | null {
    return this.pendingAction
  }

  clearPendingAction(): void {
    this.pendingAction = null
  }

  executePendingAction(): PendingAction | null {
    const action = this.pendingAction
    this.clearPendingAction()
    return action
  }

  requireAuth(action: PendingAction): void {
    this.setPendingAction(action)
    // This will be handled by components to show login modal
  }
}

export const authService = AuthService.getInstance()